import React, { useEffect, useImperativeHandle, forwardRef, useRef, useState } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const Timer = forwardRef(function Timer({ seconds }, ref) {
  const { t } = useLang();
  const [remaining, setRemaining] = useState(seconds);
  const [running, setRunning] = useState(false);
  const intRef = useRef(null);

  useEffect(() => {
    setRemaining(seconds);
    setRunning(false);
    if (intRef.current) clearInterval(intRef.current);
  }, [seconds]);

  useEffect(() => {
    if (!running) return;
    intRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(intRef.current);
          setRunning(false);
          toast.success(t('timerDone'));
          try { new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=').play(); } catch (e) { console.warn('[Timer] beep failed', e); }
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(intRef.current);
  }, [running, t]);

  useImperativeHandle(ref, () => ({
    start: () => setRunning(true),
    stop: () => setRunning(false),
    reset: () => { setRunning(false); setRemaining(seconds); },
  }));

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const pct = seconds > 0 ? ((seconds - remaining) / seconds) * 100 : 0;

  return (
    <div className="flex items-center gap-4 rounded-2xl bg-white border border-black/5 p-4">
      <div className="relative h-20 w-20">
        <svg className="absolute inset-0" viewBox="0 0 36 36">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#EFEBE1" strokeWidth="3"></circle>
          <circle
            cx="18" cy="18" r="15.9" fill="none" stroke="#E29F48" strokeWidth="3"
            strokeDasharray={`${pct}, 100`} strokeLinecap="round" transform="rotate(-90 18 18)"
          ></circle>
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-display font-bold text-[#2C2420] tabular-nums">
          {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => setRunning((r) => !r)}
          data-testid="timer-toggle"
          className="inline-flex items-center gap-1 rounded-full bg-[#C05A42] text-white px-4 py-2 text-sm font-medium hover:bg-[#A04832] transition"
        >
          {running ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          {running ? t('pauseTimer') : t('startTimer')}
        </button>
        <button
          onClick={() => { setRunning(false); setRemaining(seconds); }}
          data-testid="timer-reset"
          className="inline-flex items-center gap-1 rounded-full bg-[#EFEBE1] text-[#2C2420] px-4 py-2 text-sm font-medium hover:bg-[#E3DDCF] transition"
        >
          <RotateCcw className="h-4 w-4" /> {t('resetTimer')}
        </button>
      </div>
    </div>
  );
});

export default Timer;
